<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import QRCode from 'qrcode';

import { useAlert } from 'dashboard/composables';
import ChatvidaWhatsappChannel from 'dashboard/api/channel/chatvidaWhatsappChannel';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  inboxId: { type: [String, Number], required: true },
});

const SESSION_POLL_INTERVAL = 3000;
const SESSION_STATUS = { SCAN_QR_CODE: 'SCAN_QR_CODE', WORKING: 'WORKING' };

const { t } = useI18n();

const sessionStatus = ref(null);
const phoneNumber = ref('');
const qrCodeImage = ref('');
const receives = ref({ groups: true, status: true, channels: true });
const isLoading = ref(true);
const isWorkingOnSession = ref(false);
const logoutDialogRef = ref(null);
let pollTimer = null;

const isConnected = computed(
  () => sessionStatus.value === SESSION_STATUS.WORKING
);
const isAwaitingScan = computed(
  () => sessionStatus.value === SESSION_STATUS.SCAN_QR_CODE
);

const stopPolling = () => {
  clearTimeout(pollTimer);
  pollTimer = null;
};

onBeforeUnmount(stopPolling);

const applySession = async data => {
  sessionStatus.value = data.status;
  phoneNumber.value = data.phone_number || '';
  receives.value = data.receives;
  qrCodeImage.value = data.qr_code
    ? await QRCode.toDataURL(data.qr_code, { width: 264, margin: 2 })
    : '';
};

const loadSession = async () => {
  try {
    const { data } = await ChatvidaWhatsappChannel.getSession(props.inboxId);
    await applySession(data);
  } catch (error) {
    // Uma consulta perdida nao muda nada na tela: a proxima cobre.
  }
};

// O QR expira em segundos, entao enquanto ele estiver na tela a consulta
// precisa continuar. Uma vez conectado nao ha mais o que observar.
const scheduleNextPoll = () => {
  stopPolling();
  if (isConnected.value) return;

  pollTimer = setTimeout(async () => {
    await loadSession();
    scheduleNextPoll();
  }, SESSION_POLL_INTERVAL);
};

onMounted(async () => {
  await loadSession();
  isLoading.value = false;
  scheduleNextPoll();
});

const runSessionAction = async action => {
  isWorkingOnSession.value = true;
  try {
    const { data } = await action();
    await applySession(data);
    scheduleNextPoll();
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('INBOX_MGMT.CHATVIDA_WHATSAPP.SESSION.ERROR')
    );
  } finally {
    isWorkingOnSession.value = false;
  }
};

// Regenerar o QR tambem desloga: o WAHA so entrega um codigo novo quando a
// autenticacao antiga sai, reiniciar devolve a sessao ja pareada.
const logout = () =>
  runSessionAction(() => ChatvidaWhatsappChannel.logout(props.inboxId));

const confirmLogout = () => {
  logoutDialogRef.value.close();
  logout();
};

const updateReceives = (key, value) => {
  runSessionAction(() =>
    ChatvidaWhatsappChannel.updateReceives(props.inboxId, {
      ...receives.value,
      [key]: value,
    })
  );
};
</script>

<template>
  <div class="flex flex-col gap-4 mb-6">
    <div
      class="p-4 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak"
    >
      <div class="flex gap-4 justify-between items-start">
        <div class="flex gap-3 items-start min-w-0">
          <Icon
            :icon="isConnected ? 'i-lucide-circle-check' : 'i-lucide-unplug'"
            class="mt-0.5 size-5 shrink-0"
            :class="isConnected ? 'text-n-teal-10' : 'text-n-amber-10'"
          />
          <div class="text-start">
            <p class="text-heading-3 text-n-slate-12">
              {{
                isConnected
                  ? $t('INBOX_MGMT.CHATVIDA_WHATSAPP.SESSION.CONNECTED', {
                      phoneNumber,
                    })
                  : $t('INBOX_MGMT.CHATVIDA_WHATSAPP.SESSION.DISCONNECTED')
              }}
            </p>
            <p class="mt-1 text-body-main text-n-slate-11">
              {{
                isConnected
                  ? $t('INBOX_MGMT.CHATVIDA_WHATSAPP.SESSION.CONNECTED_HINT')
                  : $t('INBOX_MGMT.CHATVIDA_WHATSAPP.SESSION.DISCONNECTED_HINT')
              }}
            </p>
          </div>
        </div>
        <div class="flex gap-2 shrink-0">
          <Button
            faded
            slate
            sm
            :disabled="isWorkingOnSession"
            :label="$t('INBOX_MGMT.CHATVIDA_WHATSAPP.SESSION.REGENERATE_QR')"
            @click="logout"
          />
          <Button
            v-if="isConnected"
            faded
            ruby
            sm
            :disabled="isWorkingOnSession"
            :label="$t('INBOX_MGMT.CHATVIDA_WHATSAPP.SESSION.LOGOUT')"
            @click="logoutDialogRef.open()"
          />
        </div>
      </div>

      <div
        v-if="isLoading || isWorkingOnSession"
        class="flex justify-center py-6"
      >
        <Spinner class="size-6" />
      </div>
      <div
        v-else-if="isAwaitingScan"
        class="flex flex-col items-center pt-6 mt-4 border-t border-n-weak"
      >
        <img
          v-if="qrCodeImage"
          :src="qrCodeImage"
          :alt="$t('INBOX_MGMT.CHATVIDA_WHATSAPP.SESSION.QR_ALT')"
          class="rounded-lg"
        />
        <p class="mt-4 text-body-main text-n-slate-11">
          {{ $t('INBOX_MGMT.CHATVIDA_WHATSAPP.SESSION.SCAN_HINT') }}
        </p>
      </div>
    </div>

    <SettingsToggleSection
      :model-value="receives.groups"
      :header="$t('INBOX_MGMT.CHATVIDA_WHATSAPP.RECEIVES.GROUPS')"
      :description="$t('INBOX_MGMT.CHATVIDA_WHATSAPP.RECEIVES.GROUPS_HELP')"
      compact
      @update:model-value="value => updateReceives('groups', value)"
    />
    <SettingsToggleSection
      :model-value="receives.status"
      :header="$t('INBOX_MGMT.CHATVIDA_WHATSAPP.RECEIVES.STATUS')"
      :description="$t('INBOX_MGMT.CHATVIDA_WHATSAPP.RECEIVES.STATUS_HELP')"
      compact
      @update:model-value="value => updateReceives('status', value)"
    />
    <SettingsToggleSection
      :model-value="receives.channels"
      :header="$t('INBOX_MGMT.CHATVIDA_WHATSAPP.RECEIVES.CHANNELS')"
      :description="$t('INBOX_MGMT.CHATVIDA_WHATSAPP.RECEIVES.CHANNELS_HELP')"
      compact
      @update:model-value="value => updateReceives('channels', value)"
    />

    <Dialog
      ref="logoutDialogRef"
      type="alert"
      :title="$t('INBOX_MGMT.CHATVIDA_WHATSAPP.SESSION.LOGOUT_DIALOG.TITLE')"
      :description="
        $t('INBOX_MGMT.CHATVIDA_WHATSAPP.SESSION.LOGOUT_DIALOG.DESCRIPTION')
      "
      :confirm-button-label="
        $t('INBOX_MGMT.CHATVIDA_WHATSAPP.SESSION.LOGOUT_DIALOG.CONFIRM')
      "
      @confirm="confirmLogout"
    />
  </div>
</template>
